import {
  fetchTransport,
  nativerTransport,
  ServerFehler,
  type Anmeldung,
  type Antwort,
  type FehlerArt,
  type ServerClient,
  type Transport,
  type Zugang,
} from '../client';
import { meldeNetzfehler } from '../netzfehler';
import { verbindung } from '../verbindung';
import { istGeraet } from '../../platform/gleis';

/**
 * Der Weg zum **Classic-Server**. Erfüllt dieselbe `ServerClient`-Schnittstelle wie der
 * Relay-Client, damit alles darüber nichts vom Modus bemerkt.
 *
 * Vier Unterschiede zum Relay-Weg, jeder davon abgelesen in `docs/protokoll-classic.md` §1:
 *
 * 1. **HTTP Basic bei jedem Aufruf.** Keine Sitzung, kein Token, kein Ablauf.
 * 2. **Kein `401`-Wiederholungsversuch.** Ohne Sitzung kann kein Token ablaufen; ein `401` ist
 *    ein falsches Passwort. Ein zweiter Versuch führte nur den Brute-Force-Schutz des Servers
 *    vor, ohne je etwas zu ändern.
 * 3. **Pfade ohne `v1`**, und die Antworten sind nackte Arrays oder Objekte — kein
 *    `{ success, root }`.
 * 4. **Kein Lebenszeichen und keine Zeitmessung.** `anmelden()` kann nur die Zugangsdaten
 *    ausprobieren.
 *
 * Der Transport ist derselbe wie beim Relay-Client: nativ auf dem Gerät, `fetch` im Browser und
 * in Tests. Ohne den nativen Weg ginge es auf dem iPad ohnehin nicht — Classic schickt keine
 * CORS-Kopfzeilen.
 */

/** 10 s, wie am alten Gerät. */
export const CLASSIC_TIMEOUT_MS = 10000;

export interface ClassicDeps {
  zugang: Zugang;
  /** Einschiebbar für Tests; sonst das `fetch` der Laufzeit. */
  fetch?: typeof fetch;
  /** Einschiebbar für Tests; sonst nativ (Gerät) bzw. `fetch` (Browser). */
  transport?: Transport;
  timeoutMs?: number;
}

/**
 * `Basic base64(utf8(login:passwort))`.
 *
 * Bewusst über `TextEncoder` statt über das alte `unescape(encodeURIComponent(...))`: Umlaute in
 * Anmeldename oder Passwort sind hier keine Ausnahme, und `btoa` allein wirft bei allem über
 * Codepoint 255. `TextEncoder` gibt es im WKWebView seit iOS 10.
 */
export function basicKopfzeile(login: string, password: string): string {
  const bytes = new TextEncoder().encode(login + ':' + password);
  let binär = '';
  for (const b of bytes) binär += String.fromCharCode(b);
  return 'Basic ' + btoa(binär);
}

export function createClassicClient(deps: ClassicDeps): ServerClient {
  const transport: Transport =
    deps.transport ??
    (deps.fetch
      ? fetchTransport(deps.fetch)
      : istGeraet()
        ? nativerTransport()
        : fetchTransport(globalThis.fetch.bind(globalThis)));
  const timeoutMs = deps.timeoutMs ?? CLASSIC_TIMEOUT_MS;
  const basis = deps.zugang.baseUrl.replace(/\/+$/, '');
  const authorization = basicKopfzeile(deps.zugang.login, deps.zugang.password);

  /**
   * Was der alte Client nie gemessen hat, behaupten wir auch nicht.
   *
   * ⚠️ `driftSeconds: 0` heisst hier **nicht** „die Uhren stimmen überein", sondern „niemand hat
   * nachgesehen" — Classic liefert keine Serverzeit. Die Oberfläche darf im Classic-Modus deshalb
   * keinen Versatz anzeigen; sie erkennt den Fall an der leeren `serverTime`.
   */
  const ANMELDUNG: Anmeldung = {
    token: '',
    clientId: '',
    role: '',
    serverTime: '',
    driftSeconds: 0,
  };

  let angemeldet: Anmeldung | null = null;
  let laufend: Promise<Anmeldung> | null = null;

  const url = (pfad: string, query?: Record<string, string | number | undefined>) => {
    const p = pfad.startsWith('/') ? pfad : '/' + pfad;
    const teile = Object.entries(query ?? {})
      .filter(([, wert]) => wert !== undefined && wert !== '')
      .map(([k, wert]) => encodeURIComponent(k) + '=' + encodeURIComponent(String(wert)));
    return basis + p + (teile.length > 0 ? '?' + teile.join('&') : '');
  };

  const roh = async (
    pfad: string,
    init: { method: string; headers?: Record<string, string>; body?: string },
    query?: Record<string, string | number | undefined>,
  ): Promise<Antwort> => {
    try {
      const antwort = await transport(url(pfad, query), {
        ...init,
        headers: { ...init.headers, authorization },
        timeoutMs,
      });
      // Geantwortet hat er — gleich was. Das beendet eine Offline-Phase (`verbindung.ts`).
      verbindung.erfolg();
      return antwort;
    } catch (fehler) {
      // Nur der erste Ausfall einer Offline-Phase kommt ins Log; die folgenden zählt die Lage.
      const ersterAusfall = verbindung.ausfall();
      const abgebrochen =
        typeof fehler === 'object' && fehler !== null && (fehler as { name?: string }).name === 'AbortError';
      if (!abgebrochen && ersterAusfall) meldeNetzfehler(fehler);
      throw new ServerFehler(
        abgebrochen ? 'zeitueberschreitung' : 'keineVerbindung',
        abgebrochen
          ? 'Der Classic-Server hat nicht innerhalb von ' + String(Math.round(timeoutMs / 1000)) + ' s geantwortet.'
          : 'Keine Verbindung zum Classic-Server: ' + String(fehler),
      );
    }
  };

  /**
   * Classic hat keine Fehlercodes im Rumpf — bei `401` kommt eine HTML-Seite des Webservers. Der Text
   * wird deshalb gekürzt mitgegeben, statt einen `code` zu erfinden, den es nicht gibt.
   */
  const fehlerAus = async (antwort: Antwort): Promise<ServerFehler> => {
    let text = '';
    try {
      text = await antwort.text();
    } catch {
      // Auch ohne lesbaren Rumpf bleibt der Statuscode die Aussage.
    }
    const art: FehlerArt = antwort.status >= 500 ? 'serverfehler' : 'abgelehnt';
    const hinweis =
      antwort.status === 401
        ? 'Anmeldename oder Passwort stimmen nicht.'
        : text.trim() === ''
          ? ''
          : text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 200);
    return new ServerFehler(
      art,
      'Der Classic-Server antwortete ' + String(antwort.status) + (hinweis ? ': ' + hinweis : ''),
      antwort.status,
    );
  };

  const aufruf = async (
    pfad: string,
    init: { method: string; headers?: Record<string, string>; body?: string },
    query?: Record<string, string | number | undefined>,
  ): Promise<unknown> => {
    const antwort = await roh(pfad, init, query);
    if (!antwort.ok) throw await fehlerAus(antwort);
    const text = await antwort.text();
    if (text.trim() === '') return null;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new ServerFehler('serverfehler', 'Antwort war kein JSON: ' + text.slice(0, 200));
    }
  };

  const anmelden = async (): Promise<Anmeldung> => {
    if (angemeldet) return angemeldet;
    if (laufend) return laufend;

    laufend = (async () => {
      // Es gibt nichts anzumelden — also wird ausprobiert. `current=true` hält die Antwort klein:
      // der Server filtert dann auf das Zeitfenster um heute.
      const antwort = await roh('/rest/osces', { method: 'GET' }, { current: 'true' });
      if (!antwort.ok) throw await fehlerAus(antwort);
      angemeldet = ANMELDUNG;
      return ANMELDUNG;
    })();

    try {
      return await laufend;
    } finally {
      laufend = null;
    }
  };

  return {
    anmelden,

    async health() {
      // Classic hat kein Lebenszeichen. Die Prüfungsliste ist das billigste, was es gibt — anders
      // als beim Relay-Client geht das aber nicht ohne Zugangsdaten.
      return aufruf('/rest/osces', { method: 'GET' }, { current: 'true' });
    },

    async get<T>(pfad: string, query?: Record<string, string | number | undefined>) {
      return (await aufruf(pfad, { method: 'GET' }, query)) as T;
    },

    async post<T>(pfad: string, rumpf: unknown) {
      return (await aufruf(pfad, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(rumpf),
      })) as T;
    },

    letzteAnmeldung: () => angemeldet,

    vergessen() {
      angemeldet = null;
    },
  };
}
